import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import CartSheet from "@/components/CartSheet";
import { MENU_META } from "@/pages/MenuLanding";
import { Plus, Minus, ShoppingBag, Wine, ArrowLeft } from "@phosphor-icons/react";

const CATEGORY_ORDER = [
  "Grazing",
  "Starters",
  "Something More",
  "Kids",
  "Dessert",
  "To Share",
  "Mains",
  "Sides",
  "Desserts",
  "Sparkling & Rosé",
  "White",
  "Red & Fortified",
  "Beer, Cider & RTDs",
  "Spirits",
  "Cocktails",
  "Soft Drinks, Juices & Alcohol Free",
];

const CATEGORY_SUBTITLE = {
  Grazing: "Every tasting pairs beautifully with our grazing selection.",
};

export default function MenuPage() {
  const { menuType } = useParams();
  const navigate = useNavigate();
  const meta = MENU_META[menuType] || MENU_META.cellar_door;
  const {
    add,
    setQty,
    qtyOf,
    count,
    customerName,
    setCustomerName,
    tableNumber,
    setTableNumber,
    kitchenNotes,
    setKitchenNotes,
  } = useCart();
  const [cartOpen, setCartOpen] = useState(false);

  const { data: menu = [], isLoading } = useQuery({
    queryKey: ["menu", menuType],
    queryFn: async () => (await api.get(`/menu?menu_type=${menuType}`)).data,
  });

  const grouped = useMemo(() => {
    const map = {};
    menu
      .filter((m) => m.available)
      .forEach((m) => {
        (map[m.category] = map[m.category] || []).push(m);
      });
    const cats = Object.keys(map).sort((a, b) => {
      const ia = CATEGORY_ORDER.indexOf(a);
      const ib = CATEGORY_ORDER.indexOf(b);
      return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
    });
    return cats.map((c) => ({ category: c, items: map[c] }));
  }, [menu]);

  const renderItemAction = (item) => {
    if (typeof item.stock === "number" && item.stock <= 0) {
      return (
        <span
          data-testid={`sold-out-action-${item.id}`}
          className="mt-5 w-full block text-center text-wine text-xs uppercase tracking-[0.2em] border border-dashed border-wine/40 rounded-full py-2.5"
        >
          Sold out
        </span>
      );
    }
    if (item.price <= 0) {
      return (
        <span className="mt-5 w-full block text-center text-[#5C544D] text-xs uppercase tracking-[0.2em] border border-dashed border-[#EAE5DE] rounded-full py-2.5">
          Ask our staff
        </span>
      );
    }
    if (qtyOf(item.id) > 0) {
      const atMax = typeof item.stock === "number" && qtyOf(item.id) >= item.stock;
      return (
        <div
          data-testid={`qty-stepper-${item.id}`}
          className="mt-5 w-full flex items-center justify-between border border-forest rounded-full px-2 py-1"
        >
          <button
            data-testid={`decrease-menu-${item.id}`}
            onClick={() => setQty(item.id, qtyOf(item.id) - 1)}
            className="h-8 w-8 rounded-full flex items-center justify-center text-forest hover:bg-forest hover:text-cream transition-colors"
            aria-label="Remove one"
          >
            <Minus size={16} weight="bold" />
          </button>
          <span data-testid={`qty-value-${item.id}`} className="font-mono-kds text-forest text-sm">
            {qtyOf(item.id)} in order
          </span>
          <button
            data-testid={`increase-menu-${item.id}`}
            onClick={() => add(item)}
            disabled={atMax}
            className="h-8 w-8 rounded-full flex items-center justify-center text-forest hover:bg-forest hover:text-cream transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-forest"
            aria-label="Add one"
          >
            <Plus size={16} weight="bold" />
          </button>
        </div>
      );
    }
    return (
      <button
        data-testid={`add-to-cart-${item.id}`}
        onClick={() => add(item)}
        className="mt-5 w-full flex items-center justify-center gap-2 border border-forest text-forest rounded-full py-2.5 text-sm font-medium hover:bg-forest hover:text-cream transition-colors duration-200"
      >
        <Plus size={16} weight="bold" /> Add to order
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-cream text-[#2D2825]">
      {/* Nav */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-cream/80 border-b border-[#EAE5DE]">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              data-testid="back-to-menus-btn"
              onClick={() => navigate("/")}
              className="h-10 w-10 rounded-full border border-[#EAE5DE] flex items-center justify-center hover:bg-[#EAE5DE] transition-colors"
              title="Back to menus"
            >
              <ArrowLeft size={18} weight="bold" />
            </button>
            <img
              src="/gledswood-wordmark.png"
              alt="Gledswood Homestead & Winery"
              className="h-10 sm:h-12 w-auto object-contain"
              data-testid="brand-logo"
            />
          </div>
          <button
            data-testid="open-cart-btn"
            onClick={() => setCartOpen(true)}
            className="relative flex items-center gap-2 bg-wine text-cream px-5 py-2.5 rounded-full text-sm font-medium hover:bg-[#5c252c] transition-colors duration-200"
          >
            <ShoppingBag size={18} weight="bold" />
            <span className="hidden sm:inline">Order</span>
            {count > 0 && (
              <span
                data-testid="cart-count-badge"
                className="absolute -top-1.5 -right-1.5 bg-forest text-cream text-[11px] font-mono-kds h-5 min-w-5 px-1 rounded-full flex items-center justify-center"
              >
                {count}
              </span>
            )}
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative">
        <div className="relative h-[52vh] min-h-[380px] w-full overflow-hidden">
          <img src={meta.image} alt={meta.title} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/45" />
          <div className="relative max-w-6xl mx-auto px-5 sm:px-8 h-full flex flex-col justify-end pb-14">
            <p className="flex items-center gap-2 text-cream/80 text-xs uppercase tracking-[0.3em] mb-4">
              <Wine size={16} weight="fill" /> {meta.tagline}
            </p>
            <h1 className="font-heading text-cream font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl max-w-2xl leading-[1.02]">
              {meta.title}
            </h1>
            <p className="text-cream/85 mt-4 max-w-xl text-base">
              {meta.subtitle}
            </p>
          </div>
        </div>
      </section>

      {/* Order details — entered before ordering */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 -mt-8 relative z-10">
        <div
          data-testid="order-details-panel"
          className="bg-white rounded-2xl border border-[#EAE5DE] shadow-[0_8px_30px_rgb(0,0,0,0.06)] p-6 sm:p-7"
        >
          <h2 className="font-heading text-2xl text-[#2D2825] mb-1">Your table details</h2>
          <p className="text-[#5C544D] text-sm mb-5">
            Please add your table number before ordering. Name is optional.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <input
              data-testid="table-number-input"
              value={tableNumber}
              onChange={(e) => setTableNumber(e.target.value)}
              placeholder="Table number *"
              className="rounded-lg border border-[#EAE5DE] bg-cream px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-wine"
            />
            <input
              data-testid="customer-name-input"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              placeholder="Your name (optional)"
              className="rounded-lg border border-[#EAE5DE] bg-cream px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-wine"
            />
            <input
              data-testid="order-notes-input"
              value={kitchenNotes}
              onChange={(e) => setKitchenNotes(e.target.value)}
              placeholder="Notes for the kitchen (optional)"
              className="rounded-lg border border-[#EAE5DE] bg-cream px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-wine"
            />
          </div>
        </div>
      </section>

      {/* Menu */}
      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-14">
        {isLoading ? (
          <p className="text-[#5C544D] font-mono-kds text-sm">Loading menu…</p>
        ) : (
          grouped.map((group, gi) => (
            <section
              key={group.category}
              className="mb-16 animate-fade-up"
              style={{ animationDelay: `${gi * 90}ms` }}
              data-testid={`menu-category-${group.category.replace(/\s+/g, "-").toLowerCase()}`}
            >
              <div className="flex items-baseline gap-4 mb-3">
                <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-[#2D2825]">
                  {group.category}
                </h2>
                <div className="flex-1 h-px bg-[#EAE5DE]" />
              </div>
              {CATEGORY_SUBTITLE[group.category] && (
                <p className="text-[#5C544D] text-base mb-8 max-w-xl italic">
                  {CATEGORY_SUBTITLE[group.category]}
                </p>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {group.items.map((item) => (
                  <article
                    key={item.id}
                    data-testid={`menu-item-${item.id}`}
                    className="group bg-white rounded-2xl border border-[#EAE5DE] shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:-translate-y-1 hover:border-wine/30 transition-transform duration-300"
                  >
                    <div className="p-6 flex flex-col h-full">
                      <div className="flex items-start justify-between gap-3">
                        <h3 className="font-heading text-xl leading-snug text-[#2D2825]">
                          {item.name}
                          {typeof item.stock === "number" && (
                            <span
                              data-testid={`stock-badge-${item.id}`}
                              className={`ml-2 align-middle inline-block text-[11px] font-mono-kds uppercase tracking-[0.12em] rounded-full px-2.5 py-0.5 ${
                                item.stock > 0
                                  ? "text-forest bg-forest/10"
                                  : "text-wine bg-wine/10"
                              }`}
                            >
                              {item.stock > 0 ? `${item.stock} left` : "Sold out"}
                            </span>
                          )}
                        </h3>
                        <span className="font-mono-kds text-wine text-lg whitespace-nowrap">
                          {item.price > 0 ? `$${item.price.toFixed(2)}` : ""}
                        </span>
                      </div>
                      <p className="text-[#5C544D] text-sm mt-2 leading-relaxed flex-1">
                        {item.description}
                      </p>
                      {renderItemAction(item)}
                    </div>
                  </article>
                ))}
              </div>
            </section>
          ))
        )}
      </main>

      <footer className="border-t border-[#EAE5DE] py-10">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-[#5C544D] text-sm">
            © {new Date().getFullYear()} Gledswood Homestead &amp; Winery · Gledswood Hills
          </p>
          <Link
            to="/admin/login"
            data-testid="staff-login-link"
            className="text-xs uppercase tracking-[0.2em] text-[#5C544D] hover:text-wine transition-colors"
          >
            Kitchen Staff Login
          </Link>
        </div>
      </footer>

      {/* Floating cart button (mobile) */}
      {count > 0 && (
        <button
          data-testid="floating-cart-btn"
          onClick={() => setCartOpen(true)}
          className="sm:hidden fixed bottom-5 left-1/2 -translate-x-1/2 z-40 bg-wine text-cream px-6 py-3.5 rounded-full shadow-lg flex items-center gap-3 font-medium"
        >
          <ShoppingBag size={18} weight="bold" />
          View order ({count})
        </button>
      )}

      <CartSheet open={cartOpen} onClose={() => setCartOpen(false)} />
    </div>
  );
}
